import React from 'react';
import {
  IconClose,
  IconButton,
  Input,
  Label,
  LabeledInput,
  Paragraph,
  FormHelp,
} from '@zillow/constellation';
import styled from 'styled-components';
import { ComposeDocumentType, SourcePage } from './types/compose-types';
import { isValidPageRange } from './utils/page-range-utils';
import {
  getActiveSourceDocumentIndex,
  isValidDocumentName,
} from './utils/compose-documents-utils';
import { ComposeActions } from './ComposeDocumentsPanel';

const NewDocumentRow = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  border-left: ${props =>
    props.isActive
      ? `0.25rem solid ` + props.theme.constellation.colors.brand
      : `0.25rem solid transparent`};
  width: 100%;
  background-color: ${props =>
    props.isActive ? props.theme.constellation.colors.blue200 : `transparent`};
`;

const FormErrorSpacer = styled.span`
  height: 1.25rem;
`;

const ComposeDocRowCentered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InputWithErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  padding-top: 1.25rem;
`;

const ComposeDocumentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  background: transparent;

  &:focus {
    outline: none;
  }
`;

type Props = ComposeActions & {
  isActive: boolean;
  composeDocument: ComposeDocumentType;
  pages: Array<SourcePage>;
  order: number;
  hasError: boolean;
};
function ComposeDocumentItem({
  isActive,
  composeDocument,
  order,
  hasError,
  onNameChange,
  onComposeDocumentSelection,
  onPageRangesChange,
  onDeleteComposeDocument,
}: Props) {
  const handleDocumentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value);
  };

  const handlePageRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPageRangesChange(e.target.value);
  };
  const hasInvalidName = hasError && !isValidDocumentName(composeDocument);
  const hasInvalidPageRange =
    hasError &&
    !isValidPageRange(
      composeDocument.sourceDocuments[
        getActiveSourceDocumentIndex(composeDocument)
      ].selectedPages,
      composeDocument.sourceDocuments[
        getActiveSourceDocumentIndex(composeDocument)
      ].sourcePages.length
    );

  return (
    <ComposeDocumentContainer key={composeDocument.id}>
      <NewDocumentRow isActive={isActive}>
        <ComposeDocRowCentered>
          <Paragraph fontType="h5">{order}</Paragraph>
        </ComposeDocRowCentered>
        <InputWithErrorContainer>
          <LabeledInput
            label={<Label>Document Name</Label>}
            input={
              <Input
                aria-label={`Document Name ${order}`}
                value={composeDocument.name}
                onFocus={() => onComposeDocumentSelection(composeDocument.id)}
                onChange={handleDocumentNameChange}
                error={hasInvalidName}
              />
            }
          />
          {hasInvalidName && (
            <FormHelp error>Please include a document name.</FormHelp>
          )}
          {!hasInvalidName && <FormErrorSpacer />}
        </InputWithErrorContainer>
        <InputWithErrorContainer>
          <LabeledInput
            label={<Label>Select Page Ranges</Label>}
            input={
              <Input
                aria-label={`Select Page Ranges Document ${order}`}
                placeholder="Eg: 1-3,5,6-8"
                value={
                  composeDocument.sourceDocuments[
                    getActiveSourceDocumentIndex(composeDocument)
                  ].selectedPages
                }
                onFocus={() => onComposeDocumentSelection(composeDocument.id)}
                onChange={handlePageRangeChange}
                error={hasInvalidPageRange}
              />
            }
          />
          {hasInvalidPageRange && (
            <FormHelp error>Please enter a valid page number.</FormHelp>
          )}
          {!hasInvalidPageRange && <FormErrorSpacer />}
        </InputWithErrorContainer>
        <ComposeDocRowCentered>
          <IconButton
            title={`Delete Document ${order}`}
            onClick={() => onDeleteComposeDocument(composeDocument.id)}
            bare
            size="sm"
            icon={<IconClose />}
          />
        </ComposeDocRowCentered>
      </NewDocumentRow>
    </ComposeDocumentContainer>
  );
}

export default ComposeDocumentItem;
